import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  DocumentTextIcon,
  HeartIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { EditorFieldGroup } from '../../../components/workspaces/EntityWorkspacePrimitives';
import dayjs from 'dayjs';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { type ContactSearchState } from './form-sections/ContactInformation';
import type { Contact, FinixForm } from '../drawers/types/FundraisingPaymentTypes';
import {
  FundraiserInformation,
  CustomInputsInformation,
  ContactInformation,
  DedicationInformation,
  PaymentInformation,
} from './form-sections';
import {
  getProductGroupsForFrequency,
  type FundraiserLayoutProps,
  type StepNavigationProps,
  type Fund,
  type CustomInput,
  type IframeState,
  type FrequencyType,
  type DayjsLike,
} from '../drawers/types/FundraisingPaymentTypes';
import type { Merchant } from '../../types/common';
import { calculateDashboardPaymentFees } from '../../utils/dashboardFeeCalculation';
import {
  formatOfflinePaymentMethod,
  isOfflinePaymentRecord,
} from '../../utils/offlinePaymentMethods';

// Extend Window interface for PaymentForm
declare global {
  interface Window {
    PaymentForm: {
      card: (callback: (state: unknown, binInformation: unknown) => void) => FinixForm;
    };
  }
}

// Helper function to get current time in the merchant's timezone (or local timezone as fallback)
const getCurrentTimeInTimezone = (merchant: Merchant | null): string => {
  const timezone = merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Date().toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Helper function to get the effective timezone
const getEffectiveTimezone = (merchant: Merchant | null): string => {
  return merchant?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// // Navigation buttons for form steps
const StepNavigation: React.FC<StepNavigationProps> = ({ currentStep, onBack, onContinue }) => {
  // Determine if Continue button should be shown
  // Show Continue on all steps except payment_information (last step)
  const shouldShowContinue = currentStep !== 'payment_information';

  return (
    <div className="mt-4 flex w-full justify-between">
      {/* Back Button */}
      <div>
        {currentStep !== 'form_information' && (
          <Button onClick={onBack} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <ArrowLeftIcon className="h-[14px] w-[14px] text-ink-muted" />
            <div>Back</div>
          </Button>
        )}
      </div>

      {/* Continue Button */}
      <div>
        {shouldShowContinue && (
          <Button onClick={onContinue} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <div>Continue</div>
            <ArrowRightIcon className="h-[14px] w-[14px] text-ink-muted" />
          </Button>
        )}
      </div>
    </div>
  );
};

interface PaymentEditorSectionProps {
  presentation?: 'drawer' | 'workspace';
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  hasErrors: boolean;
  active: boolean;
  onClick: () => void;
  keepMounted?: boolean;
  children: React.ReactNode;
}

const PaymentEditorSection = ({
  presentation,
  title,
  description,
  icon,
  hasErrors,
  active,
  onClick,
  keepMounted = false,
  children,
}: PaymentEditorSectionProps) => {
  if (presentation === 'workspace') {
    return (
      <EditorFieldGroup
        title={title}
        description={description}
        icon={icon}
        hasErrors={hasErrors}
        enabled
      >
        <div className="[&_[data-field-surface]]:!bg-white [&_input:not([type=radio]):not([type=checkbox])]:!bg-white [&_select]:!bg-white [&_textarea]:!bg-white">
          {children}
        </div>
      </EditorFieldGroup>
    );
  }

  const content = keepMounted ? (
    <div className={active ? '' : 'hidden'}>{children}</div>
  ) : active ? (
    children
  ) : null;

  return (
    <>
      <SectionHeader title={title} hasErrors={hasErrors} onClick={onClick} />
      {content}
    </>
  );
};

export default function FundraiserLayout({
  presentation,
  donationAmount,
  formData,
  fees,
  feeCovered,
  setFeeCovered,
  merchant,
  linkedSubscription,
  handleCustomAmountChange,
  handleFeeCoverageChange,
  setFormData,
  paymentError,
  invalidFields,
  setInvalidFields,
  handleFieldFocus,
  stripeCardRef,
  finixCardRef,
  finixBankRef,
  setPaymentMethod,
  paymentMethod,
  form,
  setForm,
  setTokenID,
  isDrawerOpen,
  currentStep,
  setCurrentStep,
  handleBack,
  handleContinue,
  isEditMode,
  transaction,
  setFees,
  selectedContact,
  setSelectedContact,
  isNewContact,
  setIsNewContact,
}: FundraiserLayoutProps) {
  // Form custom inputs - used when selecting a fundraiser to populate fund customInputs
  const [formCustomInputs, setFormCustomInputs] = useState<Record<string, CustomInput[]>>({});

  // Recurring donation date/time state - passed to FundraiserInformation
  const [startValues, setStartValues] = useState<(Date | null)[]>([null]);
  const [endValues, setEndValues] = useState<(Date | string | null)[]>([null]);
  const [timeValues, setTimeValues] = useState<(string | null)[]>([null]);

  // Payment type state - passed to FundraiserInformation
  const [paymentType, setPaymentType] = useState<string>(formData.paymentType || 'Online');

  // Finix iframe state - used for reinitializing payment form on drawer close
  const [iframeStates, setIframeStates] = useState<IframeState | null>(null);

  // Contact search state - lifted from ContactInformation for state persistence across step navigation
  const [contactSearchTerm, setContactSearchTerm] = useState<string>('');
  const [contactSearchResults, setContactSearchResults] = useState<Contact[]>([]);
  const [contactHasSearched, setContactHasSearched] = useState<boolean>(false);
  const [contactSelectedEmailIndex, setContactSelectedEmailIndex] = useState<number>(0);
  const [contactSelectedPhoneIndex, setContactSelectedPhoneIndex] = useState<number>(0);
  const [contactSelectedAddressIndex, setContactSelectedAddressIndex] = useState<number>(0);
  const [contactIsAddingNewEmail, setContactIsAddingNewEmail] = useState<boolean>(false);
  const [contactIsAddingNewPhone, setContactIsAddingNewPhone] = useState<boolean>(false);
  const [contactIsAddingNewAddress, setContactIsAddingNewAddress] = useState<boolean>(false);

  // Create the contactSearchState object to pass to ContactInformation
  const contactSearchState: ContactSearchState = {
    searchTerm: contactSearchTerm,
    setSearchTerm: setContactSearchTerm,
    contacts: contactSearchResults,
    setContacts: setContactSearchResults,
    hasSearched: contactHasSearched,
    setHasSearched: setContactHasSearched,
    selectedEmailIndex: contactSelectedEmailIndex,
    setSelectedEmailIndex: setContactSelectedEmailIndex,
    selectedPhoneIndex: contactSelectedPhoneIndex,
    setSelectedPhoneIndex: setContactSelectedPhoneIndex,
    selectedAddressIndex: contactSelectedAddressIndex,
    setSelectedAddressIndex: setContactSelectedAddressIndex,
    isAddingNewEmail: contactIsAddingNewEmail,
    setIsAddingNewEmail: setContactIsAddingNewEmail,
    isAddingNewPhone: contactIsAddingNewPhone,
    setIsAddingNewPhone: setContactIsAddingNewPhone,
    isAddingNewAddress: contactIsAddingNewAddress,
    setIsAddingNewAddress: setContactIsAddingNewAddress,
  };

  // ============================================================================
  // Payment Information State - lifted from PaymentInformation for state control
  // ============================================================================

  // Stripe initialization state
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  // Error handling state
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [sendingData, setSendingData] = useState<boolean>(false);

  // Bank account state
  const [accountNumber, setAccountNumber] = useState<string>(formData.accountNumber || '');
  const [routingNumber, setRoutingNumber] = useState<string>(formData.routingNumber || '');
  const [accountType, setAccountType] = useState<'CHECKING' | 'SAVINGS'>(
    formData.accountType === 'CHECKING' || formData.accountType === 'SAVINGS'
      ? formData.accountType
      : 'CHECKING',
  );

  // Offline payment fee state
  const [offlineFeeCovered, setOfflineFeeCovered] = useState<boolean>(formData.feeCovered || false);
  const [offlineCustomFeeAmount, setOfflineCustomFeeAmount] = useState<string>(
    formData.customFeeAmount ? String(formData.customFeeAmount) : '',
  );

  // Payment date and time state (for offline payments)
  const [paymentDate, setPaymentDate] = useState<Date>(
    formData.timestamp ? new Date(formData.timestamp) : new Date(),
  );
  const [paymentTime, setPaymentTime] = useState<string>(getCurrentTimeInTimezone(merchant));

  // Track if this is the initial mount to prevent resetting state on navigation
  const isInitialMount = useRef(true);
  const prevPaymentType = useRef(paymentType);

  // ============================================================================
  // Payment Information Effects
  // ============================================================================

  // Initialize Stripe when merchant has Stripe account
  useEffect(() => {
    if (merchant && merchant.stripeAccountID) {
      const stripePublishableKey = import.meta.env.VITE_APP_STRIPE_PK;
      if (!stripePublishableKey) {
        console.error('VITE_APP_STRIPE_PK is not configured. Stripe payment method will not work.');
        return;
      }
      setStripePromise(
        loadStripe(stripePublishableKey, {
          stripeAccount: merchant.stripeAccountID,
        }),
      );
    }
  }, [merchant]);

  // Initialize the Finix payment form when drawer opens
  useEffect(() => {
    if (!isDrawerOpen || form) return;

    const initializeForm = () => {
      try {
        const newForm = window.PaymentForm.card((state) => {
          if (state !== undefined) {
            setIframeStates(state as IframeState);
          }
        });
        setForm(newForm);
      } catch (error) {
        console.error('Error initializing payment form:', error);
        setErrorMessage('Failed to initialize payment form');
        setShowErrorAlert(true);
      }
    };

    initializeForm();
  }, [isDrawerOpen, form, setForm]);

  // Sync bank account state to formData
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      accountNumber: accountNumber,
      routingNumber: routingNumber,
      accountType: accountType,
    }));
  }, [accountNumber, routingNumber, accountType]);

  // Update paymentTime when merchant timezone becomes available
  useEffect(() => {
    if (!isEditMode && isDrawerOpen && merchant?.timezone) {
      setPaymentTime(getCurrentTimeInTimezone(merchant));
    }
  }, [merchant?.timezone, isDrawerOpen, isEditMode]);

  // Reset offline fee state when payment type actually changes (not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevPaymentType.current = paymentType;
      return;
    }

    if (prevPaymentType.current === paymentType) {
      return;
    }
    prevPaymentType.current = paymentType;

    if (isEditMode) return;

    // Reset offline-specific state
    setOfflineFeeCovered(false);
    setOfflineCustomFeeAmount('');

    // Set feeCovered based on payment type: true for Online, false for offline
    const isOnline = paymentType === 'Online';
    setFormData((prevData) => ({
      ...prevData,
      feeCovered: isOnline,
      customFeeAmount: '',
    }));
  }, [paymentType, isEditMode]);

  // Populate state from transaction in edit mode
  useEffect(() => {
    if (isEditMode && transaction) {
      const isOfflinePayment = isOfflinePaymentRecord(transaction);

      if (transaction?.importedTransaction) {
        setPaymentType(
          transaction?.paymentMethod ? formatOfflinePaymentMethod(transaction.paymentMethod) : '',
        );
        setOfflineFeeCovered(transaction?.coverFees === 'Yes' || transaction?.coverFees === true);
        setOfflineCustomFeeAmount(
          transaction?.coverFees === 'Yes' || transaction?.coverFees === true
            ? String(transaction?.fees ?? '')
            : '',
        );
        if (transaction?.timestamp) {
          const timestamp = parseTimestamp(transaction.timestamp);
          setPaymentDate(timestamp);
          setPaymentTime(extractTimeFromTimestamp(timestamp, merchant));
        }
      } else if (isOfflinePayment) {
        const paymentTypeFromMethod = transaction.paymentMethod
          ? formatOfflinePaymentMethod(transaction.paymentMethod)
          : 'Other';
        setPaymentType(paymentTypeFromMethod);
        setOfflineFeeCovered(transaction?.coverFees === 'Yes' || transaction?.coverFees === true);
        setOfflineCustomFeeAmount(
          transaction?.coverFees === 'Yes' || transaction?.coverFees === true
            ? String(transaction?.fees ?? '')
            : '',
        );
        if (transaction?.timestamp) {
          const timestamp = parseTimestamp(transaction.timestamp);
          setPaymentDate(timestamp);
          setPaymentTime(extractTimeFromTimestamp(timestamp, merchant));
        }
      } else {
        setPaymentType('Online');
      }
    }
  }, [isEditMode, transaction, merchant]);

  // Reset payment state when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      setPaymentType('Online');
      setOfflineFeeCovered(false);
      setOfflineCustomFeeAmount('');
      setPaymentDate(new Date());
      setPaymentTime(getCurrentTimeInTimezone(merchant));
      setAccountNumber('');
      setRoutingNumber('');
      setAccountType('CHECKING');
      setErrorMessage('');
      setShowErrorAlert(false);
      setSendingData(false);
      setIframeStates(null);
      // Reset the initial mount tracker
      isInitialMount.current = true;
    }
  }, [isDrawerOpen, merchant]);

  // ============================================================================
  // Payment Information Helper Functions
  // ============================================================================

  // Helper function to parse timestamp
  const parseTimestamp = (timestampStr: string): Date => {
    let str = timestampStr;
    if (!str.includes('Z') && !str.includes('+') && !str.match(/\d{2}:\d{2}:\d{2}\.\d+-/)) {
      str = str.replace(' ', 'T');
      if (!str.endsWith('Z')) {
        str += 'Z';
      }
    }
    return new Date(str);
  };

  // Helper function to extract time from timestamp
  const extractTimeFromTimestamp = (timestamp: Date, merchantData: Merchant | null): string => {
    const timezone = getEffectiveTimezone(merchantData);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const timeParts = formatter.formatToParts(timestamp);
    const hours = timeParts.find((p) => p.type === 'hour')?.value || '00';
    const minutes = timeParts.find((p) => p.type === 'minute')?.value || '00';
    return `${hours}:${minutes}`;
  };

  // Helper function to combine date and time in the merchant's timezone
  const applyPaymentTimeToDate = (date: Date | null, time: string | null): string | Date | null => {
    if (!date || !time) return date;

    try {
      const [hours, minutes] = time.split(':').map(Number);
      const timezone = getEffectiveTimezone(merchant);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateTimeString = `${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(
        minutes,
      ).padStart(2, '0')}:00`;

      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });

      const testDate = new Date(dateTimeString);
      const parts = formatter.formatToParts(testDate);
      const offsetPart = parts.find((p) => p.type === 'timeZoneName');

      if (offsetPart) {
        const offsetMatch = offsetPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
        if (offsetMatch) {
          const sign = offsetMatch[1];
          const offsetHours = offsetMatch[2].padStart(2, '0');
          const offsetMinutes = offsetMatch[3] || '00';
          const isoString = `${dateTimeString}${sign}${offsetHours}:${offsetMinutes}`;
          return new Date(isoString).toISOString();
        }
      }

      // Fallback
      const targetDate = new Date(date);
      targetDate.setHours(hours, minutes, 0, 0);
      return targetDate.toISOString();
    } catch (error) {
      console.error('Error applying payment time to date:', error);
      return date;
    }
  };

  // Helper function to check if selected time is in the future for today's date
  const isTimeInFuture = (selectedTime: string | null): boolean => {
    if (!selectedTime) return false;
    const currentTime = getCurrentTimeInTimezone(merchant);
    return selectedTime > currentTime;
  };

  // Helper function to check if date is today
  const isDateToday = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    const selectedDate = new Date(date);
    return (
      selectedDate.getDate() === today.getDate() &&
      selectedDate.getMonth() === today.getMonth() &&
      selectedDate.getFullYear() === today.getFullYear()
    );
  };

  // Helper function to check for iframe/card errors
  const checkIframeErrors = (): string[] => {
    if (!iframeStates) return ['Card information is incomplete'];
    if (Array.isArray(iframeStates.errors) && iframeStates.errors.length > 0) {
      return iframeStates.errors;
    }
    if (iframeStates.complete === false) {
      return ['Card information is incomplete'];
    }
    return [];
  };

  // Card submission function
  const updateWithCard = () => {
    return new Promise((resolve, reject) => {
      setSendingData(true);
      const iframeErrors = checkIframeErrors();

      if (iframeErrors.length > 0) {
        setErrorMessage("Your card's information is incomplete");
        setSendingData(false);
        reject(new Error('Card information incomplete'));
        return;
      }

      setShowErrorAlert(false);
      setErrorMessage('');

      if (!form) {
        setErrorMessage('Payment form not initialized');
        setSendingData(false);
        reject(new Error('Payment form not initialized'));
        return;
      }

      try {
        form.submit(
          import.meta.env.VITE_APP_FINIX_ENVIRONMENT,
          import.meta.env.VITE_APP_FINIX_APPLICATION_ID,
          function (err: unknown, res: { data: { id: string } }) {
            if (err) {
              setErrorMessage(err instanceof Error ? err.message : String(err));
              setSendingData(false);
              reject(err);
              return;
            }

            const tokenID = res.data.id;
            setTokenID(tokenID);
            setSendingData(false);
            resolve(tokenID);
          },
        );
      } catch (err) {
        setErrorMessage('Something went wrong when submitting your request');
        setSendingData(false);
        reject(err);
      }
    });
  };

  // Fee coverage toggle handler
  const handleFeeCoverageToggle = (covered: boolean) => {
    setFeeCovered(covered);
    handleFeeCoverageChange(covered, fees);
  };

  // Offline fee handlers
  const handleOfflineFeeCoverageToggle = (covered: boolean) => {
    setOfflineFeeCovered(covered);
    setFormData((prevData) => ({
      ...prevData,
      feeCovered: covered,
      coverTransactionFee: covered,
    }));
  };

  const handleOfflineCustomFeeAmountChange = (amount: string) => {
    setOfflineCustomFeeAmount(amount);
    setFormData((prevData) => ({
      ...prevData,
      customFeeAmount: amount,
    }));
    setFees(parseFloat(amount) || 0);
  };

  // Payment date handler
  const handlePaymentDateChange = (date: Date) => {
    setPaymentDate(date);
    const timestamp = applyPaymentTimeToDate(date, paymentTime);
    setFormData((prevData) => ({
      ...prevData,
      timestamp: timestamp,
    }));
  };

  // Payment time handler
  const handlePaymentTimeChange = (time: string) => {
    setPaymentTime(time);
    const timestamp = applyPaymentTimeToDate(paymentDate, time);
    setFormData((prevData) => ({
      ...prevData,
      timestamp: timestamp,
    }));
  };

  // ============================================================================
  // End of Payment Information Section
  // ============================================================================

  useEffect(() => {
    // update formData to hold paymentType
    // Skip if formData already has the same paymentType to avoid circular updates
    setFormData((prevData) => {
      if (prevData.paymentType === paymentType) {
        return prevData; // No change needed, avoid re-render
      }
      return {
        ...prevData,
        paymentType: paymentType,
      };
    });

    // Only reset frequencies to One-Time if NOT in edit mode
    // In edit mode, we want to preserve the frequencies from the transaction
    if (!isEditMode) {
      setFormData((prevData) => ({
        ...prevData,
        coverFees: feeCovered,
        funds: prevData.funds.map((fund) => ({
          ...fund,
          frequency: 'One-Time',
          nextChargeDate: null,
          endChargeDate: null,
        })),
      }));
    }
  }, [paymentType, isEditMode]);

  const handleQuantityChange = (index: number, value: number) => {
    setFormData((prevData) => {
      const updatedFunds = [...prevData.funds];
      updatedFunds[index] = {
        ...updatedFunds[index],
        quantity: value,
      };

      // Calculate total amount across all funds (only include One-Time frequency funds)
      const totalAmount = updatedFunds.reduce((sum, fund, currentIndex) => {
        const fundAmount = fund.amount || 0;
        // Only include the amount if the frequency is "One-Time" or chargeTime is 'now'
        const isOneTime = fund.frequency === 'One-Time';
        const shouldInclude = isOneTime || fund.chargeTime === 'now';
        if (shouldInclude) {
          // Use the updated quantity for the current fund being changed
          const quantity = currentIndex === index ? value : fund.quantity;
          return sum + (parseFloat(String(fundAmount)) * quantity || 0);
        }
        return sum;
      }, 0);

      // Pass total to parent
      handleCustomAmountChange(totalAmount);

      return {
        ...prevData,
        funds: updatedFunds,
      };
    });
  };

  // Reset local states when drawer opens in create mode
  useEffect(() => {
    if (!isDrawerOpen) return; // Only run when drawer opens

    // Reset non-contact states when opening in create mode (not edit mode)
    // Contact-related states are handled by ContactInformation component
    // Bank/payment date states are handled by PaymentInformation component
    if (!isEditMode) {
      setSelectedContact(null);
      setIsNewContact(false);
      setPaymentType('Online');
      setStartValues([null]);
      setEndValues([null]);
      setTimeValues([null]);
    }
  }, [isDrawerOpen, isEditMode]);

  // Contact search and edit mode contact fetch are now handled by ContactInformation component

  useEffect(() => {
    if (isEditMode && transaction?.customInputs && transaction.customInputs.length > 0) {
      // In edit mode, use transaction custom inputs
      setFormData((prevData) => ({
        ...prevData,
        globalCustomInputs: transaction.customInputs
          ?.filter((input: CustomInput) => input.inputType === 'global')
          .map((input: CustomInput) => ({
            ...input,
            value: input.answers?.[0] || '', // Use the first answer from transaction
            title: input.title || input.label || '',
            options: input.options || [],
          })) as CustomInput[],
      }));
    } else if (
      !isEditMode &&
      merchant?.globalCustomInputs &&
      (merchant.globalCustomInputs as CustomInput[]).length > 0
    ) {
      // In create mode, use merchant custom inputs
      setFormData((prevData) => ({
        ...prevData,
        globalCustomInputs: (merchant?.globalCustomInputs as CustomInput[]).map(
          (input: CustomInput, index: number) => {
            const inputKey = input.id || input.title || input.label || `global-custom-${index}`;
            const existingInput = (prevData.globalCustomInputs ?? [])
              .filter(Boolean)
              .find(
                (currentInput: CustomInput, currentIndex: number) =>
                  (currentInput.id ||
                    currentInput.title ||
                    currentInput.label ||
                    `global-custom-${currentIndex}`) === inputKey,
              );

            return {
              ...input,
              value: existingInput?.value ?? input.value ?? '', // Preserve typed values if this effect reruns
              title: input.title || input.label || '',
              options: input.options || [],
            };
          },
        ),
      }));
    }
  }, [merchant?.globalCustomInputs, transaction?.customInputs, isEditMode]);

  // Populate formCustomInputs from formToFormDataMap when drawer opens
  useEffect(() => {
    if (!isDrawerOpen) return;

    if (formData?.formToFormDataMap && Object.keys(formData.formToFormDataMap).length > 0) {
      const formsArray = Object.values(formData.formToFormDataMap);

      if (formsArray.length > 0) {
        // Build formCustomInputs from the form data
        const customInputsMap: Record<string, CustomInput[]> = {};
        formsArray.forEach((form) => {
          if (form.customInputs) {
            customInputsMap[form.formName] = form.customInputs;
          }
        });
        setFormCustomInputs(customInputsMap);
      }
    }
  }, [formData?.formToFormDataMap, isDrawerOpen]);

  // handleAddressChange is now handled by ContactInformation component

  // Update handleAmountChange function
  const handleAmountChange = (index: number, value: string | number) => {
    // First validate the input with regex
    const regex = /^\d*\.?\d{0,2}$/;
    const valueStr = String(value);
    if (valueStr === '' || regex.test(valueStr)) {
      // Update local state
      setFormData((prevData) => {
        const updatedFunds = [...prevData.funds];

        updatedFunds[index] = {
          ...updatedFunds[index],
          amount: value,
          // fundFees: fundFees, // Update fees based on new amount
        };
        return {
          ...prevData,
          funds: updatedFunds,
        };
      });

      // Calculate total amount across all funds (only include One-Time frequency funds)
      const totalAmount = formData.funds.reduce((sum, fund, currentIndex) => {
        const fundAmount = currentIndex === index ? value : fund.amount || 0;
        // Only include the amount if the frequency is "One-Time" or chargeTime is 'now'
        if (currentIndex === index) {
          // For the current fund being updated, check if it's One-Time
          const currentFund = formData.funds[index];
          const isOneTime = currentFund.frequency === 'One-Time';
          const shouldInclude = isOneTime || currentFund.chargeTime === 'now';
          if (shouldInclude) {
            return sum + (parseFloat(String(fundAmount)) * fund.quantity || 0);
          }
        } else {
          // For other funds, only include if they are One-Time
          const isOneTime = fund.frequency === 'One-Time';
          const shouldInclude = isOneTime || fund.chargeTime === 'now';
          if (shouldInclude) {
            return sum + (parseFloat(String(fundAmount)) * fund.quantity || 0);
          }
        }
        return sum;
      }, 0);
      // Pass total to parent
      handleCustomAmountChange(totalAmount);
    }
  };

  // Add frequency change handler
  const handleFrequencyChange = (index: number, frequency: FrequencyType) => {
    // Get the current fund to check for pre-populated defaultScheduledRange dates
    const currentFund = formData.funds[index];
    const hasPrePopulatedDates =
      currentFund?.nextChargeDate || currentFund?.endChargeDate || currentFund?.chargeTime;

    // Update local state
    setFormData((prevData) => {
      const updatedFunds = [...prevData.funds];
      const fund = updatedFunds[index];

      // Check if product group/product need to be reset for the new frequency
      const fundraiserData = prevData?.formToFormDataMap?.[fund.fundraiser];
      const isProductType = fundraiserData?.fundraiserType === 'PRODUCT';
      const newEffectiveGroups = getProductGroupsForFrequency(fundraiserData, frequency);
      const matchingGroup = fund.productGroup
        ? newEffectiveGroups?.find((g) => g.productGroupName === fund.productGroup)
        : undefined;
      const currentGroupStillExists = !fund.productGroup || !!matchingGroup;

      // Check if the selected product still exists within the matching group
      const currentProduct = typeof fund.product === 'object' && fund.product ? fund.product : null;
      const currentProductStillExists =
        !currentProduct ||
        (matchingGroup?.products?.some((p) => p.productID === currentProduct.productID) ?? false);

      updatedFunds[index] = {
        ...fund,
        frequency,
        // For recurring: preserve pre-populated chargeTime if exists, otherwise default to 'now'
        chargeTime:
          frequency !== 'One-Time'
            ? hasPrePopulatedDates && fund.chargeTime
              ? fund.chargeTime
              : 'now'
            : null,
        // Reset endChargeDate when frequency changes — the date may not be valid for the new picker mode
        endChargeDate: frequency === 'One-Time' ? null : null,
        ...(frequency === 'One-Time' && {
          nextChargeDate: null,
          chargeTime: null,
        }),
        // Reset product group if it doesn't exist in the new frequency's groups
        ...(!currentGroupStillExists && {
          productGroup: '',
          product: '',
          ...(isEditMode ? {} : { amount: '' }),
        }),
        // Reset product (and amount) if group exists but the product within it changed
        ...(isProductType &&
          currentGroupStillExists &&
          !currentProductStillExists && {
            product: '',
            ...(isEditMode ? {} : { amount: '' }),
          }),
      };

      return {
        ...prevData,
        funds: updatedFunds,
      };
    });

    // Clear endValues local state when frequency changes so the date picker resets
    setEndValues((prev) => {
      const newState = [...prev];
      newState[index] = null;
      return newState;
    });

    // When switching to a recurring frequency and we have pre-populated dates from defaultScheduledRange,
    // sync them to the RecurringDatePicker state arrays. We DON'T update startValues/endValues here
    // because the RecurringDatePicker receives dayjs-converted values at render time.
    // Only sync the timeValue since it's just a string.
    if (frequency !== 'One-Time' && hasPrePopulatedDates) {
      // Sync chargeTime to timeValues (string, doesn't need conversion)
      if (currentFund.chargeTime) {
        setTimeValues((prev) => {
          const newState = [...prev];
          newState[index] = currentFund.chargeTime;
          return newState;
        });
      }
    }

    // Recalculate total amount after frequency change (only include One-Time frequency funds)
    const totalAmount = formData.funds.reduce((sum, fund, currentIndex) => {
      const fundAmount = fund.amount || 0;
      // Only include the amount if the frequency is "One-Time" or chargeTime is 'now'
      if (currentIndex === index) {
        // For the current fund being updated, check the new frequency
        const isOneTime = frequency === 'One-Time';
        const shouldInclude = isOneTime || fund.chargeTime === 'now';
        if (shouldInclude) {
          return sum + (parseFloat(String(fundAmount)) * fund.quantity || 0);
        }
      } else {
        // For other funds, only include if they are One-Time
        const isOneTime = fund.frequency === 'One-Time';
        const shouldInclude = isOneTime || fund.chargeTime === 'now';
        if (shouldInclude) {
          return sum + (parseFloat(String(fundAmount)) * fund.quantity || 0);
        }
      }
      return sum;
    }, 0);

    // Pass total to parent
    handleCustomAmountChange(totalAmount);
  };

  // Unified handler for fund field changes - used by FundraiserInformation
  const handleFundChange = (index: number, field: string, value: string | number) => {
    const invalidFieldByFundField: Record<string, string> = {
      fundraiser: `fund-fundraiser-${index}`,
      fundName: `fund-name-${index}`,
      amount: `fund-amount-${index}`,
      productGroup: `fund-product-group-${index}`,
      product: `fund-product-${index}`,
      quantity: `fund-quantity-${index}`,
      frequency: `fund-frequency-${index}`,
    };
    const invalidField = invalidFieldByFundField[field];
    if (invalidField) {
      handleFieldFocus(invalidField);
    }

    if (field === 'amount') {
      handleAmountChange(index, value);
    } else if (field === 'quantity') {
      handleQuantityChange(index, typeof value === 'number' ? value : parseInt(String(value), 10));
    } else if (field === 'frequency') {
      handleFrequencyChange(index, value as FrequencyType);
    } else if (field === 'fundraiser') {
      const fundraiserData = formData?.formToFormDataMap?.[String(value)];

      if (!fundraiserData) {
        console.warn(`No fundraiser data found for value: ${value}`);
        return;
      }

      const currentFrequency = formData.funds[index]?.frequency || 'One-Time';
      const effectiveGroups = getProductGroupsForFrequency(fundraiserData, currentFrequency);
      const hasGroups =
        fundraiserData?.fundraiserType === 'PRODUCT' &&
        Array.isArray(effectiveGroups) &&
        effectiveGroups.length > 0;
      const shouldDisableInEditMode = isEditMode && hasGroups;

      if (shouldDisableInEditMode && paymentType === 'Online') {
        return;
      }

      const resetSponsershipGroup = hasGroups ? formData.funds?.[index]?.productGroup ?? '' : '';

      setFormData((prevData) => {
        const updatedFunds = [...prevData.funds];
        const currentFund = updatedFunds[index];

        updatedFunds[index] = {
          ...updatedFunds[index],
          [field]: String(value),
          fundName: '',
          formID: fundraiserData.formID ?? '',
          fundraiserHasSponsershipGroups: hasGroups,
          productGroup: resetSponsershipGroup,
          product: '',
          amount: isEditMode ? currentFund.amount : '',
          customInputs:
            formCustomInputs[String(value)]?.map((input: CustomInput) => ({
              ...input,
              value: '',
              title: input.title || input.label || '',
              options: input.options || [],
            })) || [],
        };
        return {
          ...prevData,
          funds: updatedFunds,
        };
      });

      if (fundraiserData.showScheduleDonation && fundraiserData.defaultScheduledRange?.enabled) {
        const scheduleRange = fundraiserData.defaultScheduledRange;
        const scheduleType = scheduleRange.scheduleType || 'immediate';
        const startDateOffset = parseInt(String(scheduleRange.startDateOffset), 10) || 0;
        const endDateOffset = parseInt(String(scheduleRange.endDateOffset), 10) || 0;
        const scheduledTime = scheduleRange.scheduledTime || null;

        const unitMap: Record<string, 'day' | 'week' | 'month' | 'year'> = {
          days: 'day',
          weeks: 'week',
          months: 'month',
          years: 'year',
        };
        const unit = unitMap[scheduleType] || 'day';

        let calculatedStartDate: Date | null = null;
        let calculatedEndDate: Date | null = null;
        let effectiveChargeTime: string | null = null;

        if (scheduleType === 'immediate') {
          calculatedStartDate = null;
          calculatedEndDate = null;
          effectiveChargeTime = 'now';
        } else {
          if (startDateOffset > 0) {
            calculatedStartDate = dayjs().add(startDateOffset, unit).toDate();
          }
          if (endDateOffset > 0) {
            calculatedEndDate = dayjs().add(endDateOffset, unit).toDate();
          }
          effectiveChargeTime =
            scheduledTime && scheduledTime.trim() !== '' ? scheduledTime : 'now';
        }

        setFormData((prevData) => ({
          ...prevData,
          funds: prevData.funds.map((fund, inx) =>
            inx === index
              ? {
                  ...fund,
                  nextChargeDate: calculatedStartDate,
                  endChargeDate: calculatedEndDate,
                  chargeTime: effectiveChargeTime,
                }
              : fund,
          ),
        }));
      }
    } else if (field === 'productGroup') {
      setFormData((prevData) => {
        const updatedFunds = [...prevData.funds];
        updatedFunds[index] = {
          ...updatedFunds[index],
          productGroup: String(value),
          product: '',
        };
        return {
          ...prevData,
          funds: updatedFunds,
        };
      });
    } else {
      setFormData((prevData) => {
        const updatedFunds = [...prevData.funds];
        updatedFunds[index] = {
          ...updatedFunds[index],
          [field]: value,
        };
        return {
          ...prevData,
          funds: updatedFunds,
        };
      });
    }
  };

  // Reset offline fee state when payment type changes
  // Skip in edit mode to preserve transaction data
  useEffect(() => {
    // In edit mode, don't reset fee data - it comes from the transaction
    if (isEditMode) return;

    // For both online and offline payments, reset fee data in formData
    setFormData((prevData) => ({
      ...prevData,
      feeCovered: false,
      customFeeAmount: '',
    }));
  }, [paymentType, isEditMode]);

  // Note: Payment date/time initialization for create mode is handled by PaymentInformation component

  // Add error display component at the top of the form
  const ErrorDisplay = () => {
    if (!paymentError?.show) return null;

    return (
      <div className="mb-4 rounded-md border border-red-200  p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Please correct the following errors:
            </h3>
            <div className="mt-2 text-sm text-[#A04040]">
              <ul className="list-disc space-y-1 pl-5">
                {Array.isArray(paymentError.message) ? (
                  paymentError.message.map((msg, idx) => <li key={idx}>{msg}</li>)
                ) : (
                  <li>{paymentError.message}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Phone validation is now handled in ContactInformation component

  const calculateTotalFees = (amount: number): number => {
    return calculateDashboardPaymentFees({
      amount,
      merchant,
      paymentMethod,
      coverFees: feeCovered,
      isEditOfExistingGift: isEditMode,
      subscription: linkedSubscription,
      // An edit keeps the gift's stored fee; nothing is re-priced (U1).
      existingGift: isEditMode ? transaction : undefined,
    });
  };

  const handleTopAmountChange = () => {
    return formData.funds.reduce((sum, fund) => {
      const isOneTime = fund.frequency === 'One-Time';
      const shouldInclude = isOneTime || fund.chargeTime === 'now';
      if (shouldInclude) {
        return sum + (parseFloat(String(fund.amount)) * fund.quantity || 0);
      }
      return sum;
    }, 0);
  };

  useEffect(() => {
    const amount = handleTopAmountChange();
    const fees = calculateTotalFees(amount);

    setFormData((prev) => ({
      ...prev,
      amount: amount,
      fees: fees,
    }));
  }, [
    donationAmount,
    feeCovered,
    merchant?.dashboardPercentFee,
    merchant?.dashboardFixedFee,
    merchant?.feeAmounts?.processingPercentFee,
    merchant?.feeAmounts?.platformPercentFee,
    merchant?.feeAmounts?.processingFixedFee,
    merchant?.feeAmounts?.platformFixedFee,
    merchant?.billingPlan,
    merchant?.billingDetails,
    paymentMethod,
    formData.funds,
  ]);

  // Add this useEffect to ensure there's always at least one fund
  useEffect(() => {
    if (!formData.funds || formData.funds.length === 0) {
      setFormData((prev) => ({
        ...prev,
        funds: [
          {
            amount: '',
            fundraiser: '',
            fundName: '',
            formID: '',
            productGroup: '',
            fundraiserHasSponsershipGroups: false,
            product: '',
            customInputs: [],
            quantity: 1,
            frequency: 'One-Time',
            nextChargeDate: null,
            endChargeDate: null,
            chargeTime: null,
          },
        ],
      }));
    }
  }, [formData.funds]);

  // This is made so that when the page for contact is loaded, the details are too
  // useEffect(() => {
  //   if (isDrawerOpen && selectedContact) {
  //     updateRelatedContactDetails(selectedContact);
  //   }
  // }, [selectedContact]);

  // Add reset function
  useEffect(() => {
    if (!isDrawerOpen) {
      // Reset all form data
      setFormData((prevData) => ({
        ...prevData,
        firstName: '',
        lastName: '',
        email: '',
        contactSearch: '',
        phone: '',
        organizationName: '',
        address1: '',
        address2: '',
        city: '',
        region: '',
        zip: '',
        country: '',
        contactID: '', // Initialize contactID
        paymentType: 'Online',
        coverFees: true,
        coverTransactionFee: true,
        feeCovered: false,
        funds: [
          {
            fundName: '',
            amount: '',
            fundraiser: '',
            productGroup: '',
            fundraiserHasSponsershipGroups: false,
            product: '',
            quantity: 1,
            frequency: 'One-Time',
            formID: '',
            nextChargeDate: null,
            endChargeDate: null,
            chargeTime: null,
            customInputs: [],
          },
        ],
        globalCustomInputs: [],
        purchaserCustomInputs: [],
      }));

      // Reset local state
      setPaymentType('Online');
      setStartValues([null]);
      setEndValues([null]);
      setTimeValues([null]);
      setIframeStates(null);
      setFormCustomInputs({});

      // Reset contact selection state (passed from parent)
      setSelectedContact(null);
      setIsNewContact(false);

      // Reset contact search state (lifted from ContactInformation)
      setContactSearchTerm('');
      setContactSearchResults([]);
      setContactHasSearched(false);
      setContactSelectedEmailIndex(0);
      setContactSelectedPhoneIndex(0);
      setContactSelectedAddressIndex(0);
      setContactIsAddingNewEmail(false);
      setContactIsAddingNewPhone(false);
      setContactIsAddingNewAddress(false);

      // Reset payment-related states
      // Initialize a new Finix form if needed
      if (form) {
        try {
          // Attempt to initialize a new form instance
          const newForm = window.PaymentForm.card((state) => {
            if (state === undefined) {
              // Handle undefined state
            } else {
              setIframeStates(state);
            }
          });
          setForm(newForm);
        } catch (error) {
          console.error('Error reinitializing payment form:', error);
        }
      }

      // Clear Stripe elements if they exist
      // if (stripeCardRef.current) {
      //     stripeCardRef.current.clear();
      // }

      // Reset parent component states
      setFeeCovered(true);
    }
  }, [isDrawerOpen]);

  // Initialize payment type from transaction in edit mode
  // Note: Payment date/time and fee states are now handled by PaymentInformation component
  useEffect(() => {
    if (isEditMode && transaction) {
      const isOfflinePayment = isOfflinePaymentRecord(transaction);

      if (transaction?.importedTransaction) {
        setPaymentType(
          transaction?.paymentMethod ? formatOfflinePaymentMethod(transaction.paymentMethod) : '',
        );
      } else if (isOfflinePayment) {
        // Handle offline payment methods (CASH, CHECK, CRYPTO, PAYPAL, VENMO, etc.)
        const paymentTypeFromMethod = transaction.paymentMethod
          ? formatOfflinePaymentMethod(transaction.paymentMethod)
          : 'Other';
        setPaymentType(paymentTypeFromMethod);
      } else {
        // Online payment methods (CARD, PAY_BY_BANK, GOOGLE_PAY, APPLE_PAY, AMAZON_PAY)
        setPaymentType('Online');
      }
    }
  }, [isEditMode, transaction, transaction?.paymentMethod]);

  // Note: paymentType sync from formData is now handled by the effect above (line 1486-1552)
  // which directly reads from transaction.paymentMethod. This eliminates circular updates
  // between formData.paymentType and local paymentType state.

  // New useEffect to handle transaction data in edit mode
  useEffect(() => {
    if (isEditMode && transaction?.funds && Array.isArray(transaction.funds)) {
      // Helper function to extract time from date string
      const extractTimeFromDate = (
        dateString: string | Date | null | undefined,
        timezone: string | undefined,
      ): string | null => {
        if (!dateString) return null;

        try {
          const date = new Date(dateString);

          // For UTC timezone, use UTC methods
          if (!timezone || timezone === 'UTC') {
            const hours = date.getUTCHours().toString().padStart(2, '0');
            const minutes = date.getUTCMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
          }

          // For other timezones, try to get the local time in that timezone
          try {
            const localTime = date.toLocaleString('en-US', {
              timeZone: timezone,
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
            });

            // Extract time from the formatted string (e.g., "14:30")
            const timeMatch = localTime.match(/(\d{1,2}):(\d{2})/);
            if (timeMatch) {
              const hours = timeMatch[1].padStart(2, '0');
              const minutes = timeMatch[2];
              return `${hours}:${minutes}`;
            }
          } catch (timezoneError) {
            console.warn('Could not format time for timezone:', timezone, timezoneError);
          }

          // Fallback: use local time
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}`;
        } catch (error) {
          console.error('Error extracting time from date:', error);
          return null;
        }
      };

      // Helper function to convert frequency format
      const convertFrequency = (frequency: string | undefined): Fund['frequency'] => {
        if (!frequency) return 'One-Time';

        const frequencyMap: Record<string, Fund['frequency']> = {
          ONE_TIME: 'One-Time',
          DAILY: 'Daily',
          WEEKLY: 'Weekly',
          MONTHLY: 'Monthly',
          ANNUALLY: 'Annually',
        };

        return frequencyMap[frequency] || 'One-Time';
      };

      // Update formData with transaction funds data
      setFormData((prevData) => {
        const updatedFunds = (transaction.funds ?? []).map((fund: Fund) => {
          const frequency = convertFrequency(fund.frequency);
          const nextChargeDate = fund.nextChargeDate ? new Date(fund.nextChargeDate) : null;
          const endChargeDate =
            fund.endChargeDate && fund.endChargeDate !== 'INFINITY'
              ? new Date(fund.endChargeDate)
              : null;

          // Calculate charge time from nextChargeDate if it exists and frequency is not One-Time
          // Use transaction timezone if available, otherwise fall back to merchant timezone
          let chargeTime = fund.chargeTime;
          if (frequency !== 'One-Time' && nextChargeDate) {
            const effectiveTimezone = transaction.timezone || getEffectiveTimezone(merchant);
            chargeTime = extractTimeFromDate(fund.nextChargeDate, effectiveTimezone);
          }

          return {
            fundName: fund.fundName || '',
            amount: String(fund.amount ?? ''),
            fundraiser: fund.fundraiser || '',
            formID: fund.formID || '',
            productGroup: fund.productGroup || '',
            fundraiserHasSponsershipGroups: !!(fund.productGroup && fund.productID),
            product: fund.productID
              ? {
                  productID: fund.productID,
                  productName: fund.productName || '',
                  productPrice:
                    typeof fund.amount === 'number'
                      ? fund.amount
                      : parseFloat(String(fund.amount)) || 0,
                }
              : '',
            cartItemKey: fund.cartItemKey || '', // 🔑 Preserve cartItemKey for matching
            fundItemId: fund.fundItemId || null, // 🆔 Preserve fundItemId for matching
            customInputs: (() => {
              if (transaction?.customInputs && transaction.customInputs.length > 0) {
                // 🆔 Filter custom inputs by fundItemId to match this specific fund instance
                // This ensures funds with same formID but different answers show their own inputs
                return transaction.customInputs
                  .filter((input: CustomInput) => input.inputType === 'fund')
                  .filter((input: CustomInput) => {
                    // Priority 1: Match by fundItemId (when it exists on both sides)
                    // If both fund and input have fundItemId, use STRICT matching only
                    if (fund.fundItemId && input.fundItemId) {
                      // They must match exactly - no fallback to other methods
                      return input.fundItemId === fund.fundItemId;
                    }

                    // Priority 2: Match by cartItemKey (when fundItemId doesn't exist)
                    // Used when one or both sides lack fundItemId
                    if (fund.cartItemKey && input.cartItemKey) {
                      return input.fundId === fund.formID && input.cartItemKey === fund.cartItemKey;
                    }

                    // Priority 3: Legacy fallback - match by fundId only
                    // Used when:
                    // - Creating new payments in dashboard (no identifiers yet)
                    // - Legacy transactions (only have fundId)
                    // - Neither fundItemId nor cartItemKey available
                    return input.fundId === fund.formID;
                  })
                  .map((input: CustomInput) => ({
                    ...input,
                    value: input.answers?.[0] || '',
                    title: input.title || input.label || '',
                    options: input.options || [],
                  })) as CustomInput[];
              }
              return fund.customInputs || [];
            })(),
            quantity: fund.quantity || 1,
            frequency: frequency,
            nextChargeDate: nextChargeDate,
            endChargeDate: endChargeDate,
            chargeTime: chargeTime,
          } as Fund;
        });

        return {
          ...prevData,
          funds: updatedFunds,
        };
      });

      // Update the date selection states
      const newStartValues = new Array(transaction.funds.length).fill(null);
      const newEndValues = new Array(transaction.funds.length).fill(null);
      const newTimeValues = new Array(transaction.funds.length).fill(null);

      // Set the selection states based on whether dates exist
      transaction.funds.forEach((fund, index) => {
        if (fund.nextChargeDate) {
          newStartValues[index] = new Date(fund.nextChargeDate);
        }
        if (fund.endChargeDate && fund.endChargeDate !== 'INFINITY') {
          newEndValues[index] = new Date(fund.endChargeDate);
        }
        if (fund.chargeTime) {
          newTimeValues[index] = fund.chargeTime;
        }
      });

      setStartValues(newStartValues);
      setEndValues(newEndValues);
      setTimeValues(newTimeValues);
    }
  }, [isEditMode, transaction]);

  // Helper functions for RecurringDatePicker
  const setStartValue = (index: number, value: Date | null) => {
    setStartValues((prev) => {
      const newState = [...prev];
      newState[index] = value;
      return newState;
    });
  };

  const setEndValue = (index: number, value: Date | string | null) => {
    setEndValues((prev) => {
      const newState = [...prev];
      newState[index] = value;
      return newState;
    });

    // If value is INFINITY, update form data immediately
    if (value === 'INFINITY') {
      setFormData((prev) => ({
        ...prev,
        funds: prev.funds.map((fund, inx) =>
          inx === index
            ? {
                ...fund,
                endChargeDate: 'INFINITY',
              }
            : fund,
        ),
      }));
    }
  };

  const setTimeValue = (index: number, value: string | null) => {
    setTimeValues((prev) => {
      const newState = [...prev];
      newState[index] = value;
      return newState;
    });

    // The RecurringDatePicker now handles the time logic internally
    // and calls calculateTotalSubscriptionCharge with the appropriate time value
  };

  const calculateTotalSubscriptionCharge = (
    index: number,
    startValue: DayjsLike | Date | null,
    endValue: DayjsLike | Date | string | null,
    timeValue: string | null,
  ) => {
    // This function can be used for any subscription charge calculations
    // For now, we'll just update the form data with the selected dates
    if (startValue) {
      // Convert dayjs object to Date if needed
      const startDate: Date | string | null = (startValue as DayjsLike)?.$d
        ? ((startValue as DayjsLike).$d as Date)
        : (startValue as Date);
      setFormData((prev) => ({
        ...prev,
        funds: prev.funds.map((fund, inx) =>
          inx === index
            ? {
                ...fund,
                nextChargeDate: startDate,
              }
            : fund,
        ),
      }));
    } else {
      // Start date was cleared — only reset if the fund previously had one (skip on initial mount)
      setFormData((prev) => ({
        ...prev,
        funds: prev.funds.map((fund, inx) =>
          inx === index && fund.nextChargeDate
            ? { ...fund, nextChargeDate: null, chargeTime: 'now' }
            : fund,
        ),
      }));
    }

    if (endValue) {
      // Handle INFINITY value or convert dayjs object to Date if needed
      const endDate: Date | string | null =
        endValue === 'INFINITY'
          ? 'INFINITY'
          : (endValue as DayjsLike)?.$d
          ? ((endValue as DayjsLike).$d as Date)
          : (endValue as Date | string);
      setFormData((prev) => ({
        ...prev,
        funds: prev.funds.map((fund, inx) =>
          inx === index
            ? {
                ...fund,
                endChargeDate: endDate,
              }
            : fund,
        ),
      }));
    }

    // Update charge time - use the timeValue parameter if provided, otherwise fall back to timeValues array
    let timeString: string | null = null;
    if (timeValue !== undefined) {
      timeString = timeValue;
    } else {
      const currentTimeValue = timeValues[index] ?? null;
      if (currentTimeValue) {
        // Convert dayjs time object to string if needed - timeValues is already string | null
        timeString = currentTimeValue;
      }
    }

    if (timeString !== null) {
      setFormData((prev) => ({
        ...prev,
        funds: prev.funds.map((fund, inx) =>
          inx === index
            ? {
                ...fund,
                chargeTime: timeString,
              }
            : fund,
        ),
      }));
    }
  };

  // Helper function to convert frequency to timePicker format
  const getTimePickerFromFrequency = (frequency: FrequencyType): string => {
    switch (frequency) {
      case 'Daily':
        return 'day';
      case 'Weekly':
        return 'week';
      case 'Monthly':
        return 'month';
      case 'Annually':
        return 'year';
      default:
        return 'day';
    }
  };

  // Contact-related handlers (handleEmailChange, handlePhoneChange, toggleEmailDropdown,
  // togglePhoneDropdown, handleAddressSelection, toggleAddressDropdown, updateRelatedContactDetails,
  // handleContactSelect) are now in ContactInformation component

  // Reset function for non-contact states (contact states handled by ContactInformation)
  // Note: Bank account, payment date/time, and error states are now handled by PaymentInformation
  const resetFormStates = useCallback(() => {
    setSelectedContact(null);
    setIsNewContact(false);
    // Reset iframe states
    setIframeStates(null);
    // Reset subscription-related states
    setStartValues([null]);
    setEndValues([null]);
    setTimeValues([null]);
  }, [setSelectedContact, setIsNewContact]);

  // Reset states when transaction changes (for edit mode)
  useEffect(() => {
    if (isEditMode && transaction) {
      resetFormStates();
    }
  }, [transaction?.transactionID, isEditMode, resetFormStates]);

  // Helper function to check if a tab/step has validation errors
  const stepHasErrors = useCallback(
    (step: string): boolean => {
      if (!invalidFields || invalidFields.size === 0) return false;
      return Array.from(invalidFields).some((field) => field.startsWith(step + '-'));
    },
    [invalidFields],
  );

  return (
    <div className="space-y-4">
      {/* Add error display at the top */}
      <ErrorDisplay />

      <PaymentEditorSection
        presentation={presentation}
        title="Designations and recurring terms"
        description="Choose the form, fund, amount, quantity, and payment cadence."
        icon={DocumentTextIcon}
        hasErrors={stepHasErrors('form_information')}
        active={currentStep === 'form_information'}
        onClick={() => setCurrentStep('form_information')}
      >
        <div>
          <FundraiserInformation
            formData={formData}
            setFormData={setFormData}
            merchant={merchant}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
            handleFieldFocus={handleFieldFocus}
            isEditMode={isEditMode}
            isDrawerOpen={isDrawerOpen}
            // State and handlers for persistence across navigation
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            startValues={startValues}
            setStartValue={setStartValue}
            endValues={endValues}
            setEndValue={setEndValue}
            timeValues={timeValues}
            setTimeValue={setTimeValue}
            handleFundChange={handleFundChange}
            calculateTotalSubscriptionCharge={calculateTotalSubscriptionCharge}
            getTimePickerFromFrequency={getTimePickerFromFrequency}
          />

          {/* Navigation buttons */}
          {presentation !== 'workspace' && (
            <StepNavigation
              currentStep={currentStep}
              onBack={handleBack}
              onContinue={handleContinue}
              paymentType={paymentType}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </PaymentEditorSection>

      {/* Only show Custom Inputs Information section if there are global custom inputs */}
      {((isEditMode && formData.globalCustomInputs && formData.globalCustomInputs.length > 0) ||
        (!isEditMode &&
          merchant?.globalCustomInputs &&
          (merchant.globalCustomInputs as CustomInput[]).length > 0)) && (
        <PaymentEditorSection
          presentation={presentation}
          title="Custom Input Responses"
          description="Capture merchant and form-specific responses."
          icon={ChatBubbleLeftRightIcon}
          hasErrors={stepHasErrors('custom_input_information')}
          active={currentStep === 'custom_input_information'}
          onClick={() => setCurrentStep('custom_input_information')}
        >
          <div>
            <CustomInputsInformation
              formData={formData}
              setFormData={setFormData}
              merchant={merchant}
              isEditMode={isEditMode}
              handleFieldFocus={handleFieldFocus}
              invalidFields={invalidFields}
            />

            {/* Navigation buttons */}
            {presentation !== 'workspace' && (
              <StepNavigation
                currentStep={currentStep}
                onBack={handleBack}
                onContinue={handleContinue}
                paymentType={paymentType}
                isEditMode={isEditMode}
              />
            )}
          </div>
        </PaymentEditorSection>
      )}

      <PaymentEditorSection
        presentation={presentation}
        title="Contact Information"
        description="Select an existing contact or enter a new donor."
        icon={UserIcon}
        hasErrors={stepHasErrors('contact_information')}
        active={currentStep === 'contact_information'}
        onClick={() => setCurrentStep('contact_information')}
      >
        <div>
          <ContactInformation
            formData={formData}
            setFormData={setFormData}
            merchant={merchant}
            invalidFields={invalidFields}
            setInvalidFields={setInvalidFields}
            handleFieldFocus={handleFieldFocus}
            isDrawerOpen={isDrawerOpen}
            isEditMode={isEditMode}
            transaction={transaction}
            selectedContact={selectedContact}
            setSelectedContact={setSelectedContact}
            isNewContact={isNewContact}
            setIsNewContact={setIsNewContact}
            contactSearchState={contactSearchState}
          />

          {/* Navigation buttons */}
          {presentation !== 'workspace' && (
            <StepNavigation
              currentStep={currentStep}
              onBack={handleBack}
              onContinue={handleContinue}
              paymentType={paymentType}
              isEditMode={isEditMode}
              selectedContact={selectedContact}
              isNewContact={isNewContact}
            />
          )}
        </div>
      </PaymentEditorSection>

      {/* Dedication Section - Show if any selected form has enableDedication */}
      {(() => {
        // Check if any fund's form has dedication enabled (best case: any fund with enableDedication)
        const hasDedicationEnabled = formData.funds?.some((fund) => {
          const fundraiserData = formData?.formToFormDataMap?.[fund.fundraiser];
          return (
            fundraiserData?.enableDedication &&
            fundraiserData?.dedicationTypes &&
            fundraiserData.dedicationTypes.length > 0
          );
        });

        if (!hasDedicationEnabled) return null;

        // Merge all unique dedication types from all selected funds
        const dedicationTypesSet = new Set<string>();
        formData.funds?.forEach((fund) => {
          const fundraiserData = formData?.formToFormDataMap?.[fund.fundraiser];
          if (fundraiserData?.dedicationTypes) {
            fundraiserData.dedicationTypes.forEach((type) => dedicationTypesSet.add(type));
          }
        });
        const dedicationTypes = Array.from(dedicationTypesSet);

        // Check if dedication message is enabled (best case: any fund with enableDedicationMessage)
        const enableDedicationMessage = formData.funds?.some((fund) => {
          const fundraiserData = formData?.formToFormDataMap?.[fund.fundraiser];
          return fundraiserData?.enableDedicationMessage;
        });

        return (
          <PaymentEditorSection
            presentation={presentation}
            title="Dedication Responses"
            description="Add tribute or dedication information."
            icon={HeartIcon}
            hasErrors={stepHasErrors('dedication_information')}
            active={currentStep === 'dedication_information'}
            onClick={() => setCurrentStep('dedication_information')}
          >
            <div>
              <DedicationInformation
                formData={formData}
                setFormData={setFormData}
                handleFieldFocus={handleFieldFocus}
                invalidFields={invalidFields}
                dedicationTypes={dedicationTypes}
                enableDedicationMessage={enableDedicationMessage || false}
              />

              {presentation !== 'workspace' && (
                <StepNavigation
                  currentStep={currentStep}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  paymentType={paymentType}
                  isEditMode={isEditMode}
                />
              )}
            </div>
          </PaymentEditorSection>
        );
      })()}

      <PaymentEditorSection
        presentation={presentation}
        title="Payment Method"
        description="Choose the payment method and fee preference."
        icon={CreditCardIcon}
        hasErrors={stepHasErrors('payment_information')}
        active={currentStep === 'payment_information'}
        onClick={() => setCurrentStep('payment_information')}
        keepMounted
      >
        {/* Always render PaymentInformation to allow card iframe preloading, but hide when not on this step */}
        <div>
          <PaymentInformation
            presentation={presentation}
            formData={formData}
            setFormData={setFormData}
            merchant={merchant}
            feeCovered={feeCovered}
            handleFeeCoverageToggle={handleFeeCoverageToggle}
            invalidFields={invalidFields}
            handleFieldFocus={handleFieldFocus}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            stripeCardRef={stripeCardRef}
            finixCardRef={finixCardRef}
            finixBankRef={finixBankRef}
            form={form}
            isDrawerOpen={isDrawerOpen}
            isEditMode={isEditMode}
            paymentType={paymentType}
            stripePromise={stripePromise}
            iframeStates={iframeStates}
            updateWithCard={updateWithCard}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            routingNumber={routingNumber}
            setRoutingNumber={setRoutingNumber}
            accountType={accountType}
            setAccountType={setAccountType}
            offlineFeeCovered={offlineFeeCovered}
            handleOfflineFeeCoverageToggle={handleOfflineFeeCoverageToggle}
            offlineCustomFeeAmount={offlineCustomFeeAmount}
            handleOfflineCustomFeeAmountChange={handleOfflineCustomFeeAmountChange}
            paymentDate={paymentDate}
            handlePaymentDateChange={handlePaymentDateChange}
            paymentTime={paymentTime}
            handlePaymentTimeChange={handlePaymentTimeChange}
            isDateToday={isDateToday}
            isTimeInFuture={isTimeInFuture}
            setShowErrorAlert={setShowErrorAlert}
            setSendingData={setSendingData}
          />
          {presentation !== 'workspace' && currentStep === 'payment_information' && (
            <StepNavigation
              currentStep={currentStep}
              onBack={handleBack}
              onContinue={handleContinue}
              paymentType={paymentType}
              isEditMode={isEditMode}
            />
          )}
        </div>
      </PaymentEditorSection>
    </div>
  );
}
